/** List View Component

  * This is a general listview component, it can be used by supplying the ListView with a collection and columns
  *
  *
  * -------------------------------------------------------------
  * Collection: For example a list of devices
  *
  * -------------------------------------------------------------
  * Columns: The columns from which the list shoud be created in the following format
  *
  * Every column has the following structure
  * {
  *   label: <string> Header label of the column
  *   sortable: <boolean true|false> Indicating if the column is sortable
  *   roles: <array ['super','admin']>  Indicating the necessary role of the user
  * }
  *
  * There are 2 special column types, _bulk and _actions. When _bulk is set the system shows a checkbox which enables to
  * select the item and perform a bulk action. When _actions is enabled the system generates action buttons for each row.
  *
  * # Bulk
  * A bulk column has an attribute called options this is an object with all the options which need to be available in the dropdown list
  * As soon as an user applies a bulk action an event is triggerd called 'bulk:action' which contains the collection and the slug of the selected bulk action
  * {
  *   label: <string>
  *   options: {
  *     <slug>:{
  *       label: <string> Header label of the column
  *       roles: <array ['super','admin']>  Indicating the necessary role of the user
  *     },
  *     <slug>: ...
  *   }
  * }
  *
  * # Actions
  * Actions are shown as icons or buttons and enable a user to perform an action on the row. When click the action is "triggerd" this trigger is based
  * on the trigger attribute or if that's not set it user "item:action"
  * {
  *  label: <string>
  *   options: {
  *     <slug>:{ slug is used for generating icon classes, this shoud be unique
  *       action: <string> used for triggering
  *       roles: <array ['super','admin']> // the needed roles to see this action
  *       trigger:<string> // the trigger which is fired when the action is clicked. If no trigger is set the system triggers "item:<action>"
  *     },
  *     <slug>: ...
  *   }
  * }
  *
  * -------------------------------------------------------------
  * Full columns example
  *
  * {
  *   '_bulk': {
  *     'options': {
  *       'edit': {'label': 'Edit','roles': ['super', 'admin']},
  *       'delete': {'label': 'Delete','roles': ['super']}
  *      }
  *    },
  *    'name': {'label': 'name','sortable': true, 'roles': [] },
  *    'type': {'label': 'type','sortable': true, 'roles': []},
  *    'createdAt': {'label': 'created at','sortable': true, 'roles': []},
  *    '_actions': {
  *      'options': {
  *        'edit': { 'action': 'edit', 'roles': [] },
  *        'manage': {'action': 'manage', 'roles': []}
  *       }
  *    }
  * }
  *
  *
  *
  * -----------------------------------------------------------------
  * Using a different item view example:
  *
  * var ListItem = ListComponent.ItemView.extend({
  *   template: require('./custom/template.html'),
  * })
  * var listView = ListComponent.ListView.extend({})
  * var LayoutView = Marionette.View.extend({
  *   onRender:function() {
  *     var options = this.options
  *     options = _.extend({childView: ListItem}, options)
  *     this.showChildView('listRegion', new listView(options))
  *   }
  *   templateContext: function () {
  *     var self = this
  *     return {
  *       getListColumns: function () { return self.options.columns },
  *       getColumnField: function (field) {},
  *     }
  *   }
  * })
  */

'use strict'

var Marionette = require('marionette')
var App = require('app')
var BulkToolsBehavior = require('src/behaviors/bulk')
var CollectionPaginationBehavior = require('src/behaviors/collection_pagination')

var ListView = require('src/components/list-view')

var ItemView = Marionette.View.extend({
  tagName: 'tr',
  className: 'm-list-row',
  template: require('./templates/item.html'),
  events: {
    'click .js-bulk-select': 'onBulkSelect'
  },
  onBulkSelect: function (ev) {
    this.model.set('_bulk', ev.currentTarget.checked)
  },
  templateContext: function () {
    var self = this
    return {
      // returns the colimns
      getListColumns: function () {
        return self.options.columns
      },
      getHideClasses: App.TemplateHelpers.getHideClasses(self.options),       
      // gets called for every column, use this to customize the content of the columns
      getColumnField: function (field) {
        var value = _.get(this, field)
        switch (field) {
          case 'type':
            return App.TemplateHelpers.getDeviceTypeLabel(value)
            break
          case 'lastSeenAt':
            return App.TemplateHelpers.formatTimeFromNow(value)
            break
          case 'createdAt':
            return App.TemplateHelpers.formatTime(value)
            break
          default:
            return value
            break
        }
      }
    }
  },
  modelEvents: {
    'change': 'render'

  }
})

module.exports.ItemView = ItemView

var EmptyView = Marionette.View.extend({
  template: require('./templates/empty.html'),
  tagName: 'tr',
  className: 'm-list-empty',
  initialize: function (options) {
    this.listenTo(this.options.collection, 'sync', this.render)
  },
  templateContext: function () {
    var self = this
    return {
      isLoading: function () {
      //  if (!self.options.collection) return false
        return self.options.collection.fetching
      },
      // returns the colimns
      getTitle: function () {
        if (this.title) return this.title
        return 'Whoops! no items.'
      },
      getSubTitle: function () {
        if (this.subTitle) return this.subTitle
        return ''
      }
    }
  }
})
module.exports.EmptyView = EmptyView

var LoadingView = Marionette.View.extend({
  template: _.template('Nothing to display.')
})

// ------ //
var ListBody = Marionette.CollectionView.extend({
  tagName: 'tbody',
  childView: ItemView,

  loadingView: LoadingView,
  emptyView: EmptyView,

  buildChildView: function (child, ChildViewClass, childViewOptions) {
    // orchestrate the right arguments for the childView
    var columns = this.options.columns
    // dynamically generate the triggers for the item view
    var triggers = {}
    triggers['click'] = 'item:click'
    if (_.has(columns, '_actions.options')) {
      _.each(columns._actions.options, function (option, key) {
        var trigger = 'item:' + option.action
        if (option.trigger) trigger = option.trigger
        triggers['click .js-' + option.action] = trigger
      })
    }

    // build the final list of options for the childView class and pass the columns
    var options = _.extend({model: child, triggers: triggers}, this.options, childViewOptions)
    if (this.collection.length <= 0 && this.options.emptyViewModel) options.model = this.options.emptyViewModel

    // create the child view instance
    return new ChildViewClass(options)
  }
})

var ListView = Marionette.View.extend({
  template: require('./templates/list.html'),
  className: 'list',
  triggers: {
    'click .js-add': 'client-nodes:add'
  },
  events: {
    'click .js-bulk-select-all': 'onBulkSelectAll',
    'click .js-sort-list': 'onSort'
  },
  childViewEventPrefix: 'listview',
  collectionEvents: {
    'change': 'render'
  },
  onBulkSelectAll: function (ev) {
    this.collection.forEach(function (model, index) {
      model.set('_bulk', ev.currentTarget.checked, {silent: true})
    })
    this.collection.trigger('change', {})
  },
  onSort: function (ev) {
    this.collection.sortByField(ev.currentTarget.getAttribute('data-field'))
  },
  initialize: function () {
    // filter the columns and actions based on role etc
    var columns = _.pickBy(this.options.columns, function (o) {
      return App.session.user.hasRole(o.roles)
    })

    // filter the actions
    if (columns._actions) {
      var actions = _.pickBy(columns._actions.options, function (o) {
        return App.session.user.hasRole(o.roles)
      })
      // if after filtering there are no actions left, remove it from the columns
      if (_.size(actions) <= 0) {
        delete columns._actions
      } else {
        columns._actions.options = actions
      }
    }

    // filter the bulk
    if (columns._bulk) {
      var options = _.pickBy(columns._bulk.options, function (o) {
        return App.session.user.hasRole(o.roles)
      })

      // if after filtering there are no bulk actions left, remove it from the columns
      if (_.size(options) <= 0) {
        delete columns._bulk
      } else {
        columns._bulk.options = options
      }
    }

    this.options.columns = columns
    // this.options.noResults = columns
  },
  regions: {
    list_body: {
      el: '.list-body-region',
      replaceElement: true
    },
    bulk_tools: {
      el: '.bulk-tools-region',
      //replaceElement: true
    },
    collection_pagination: {
      el: '.pagination-region'
    }
  },
  onRender: function () {
    this.showChildView('list_body', new ListBody({
      childView: this.options.childView,
      collection: this.collection,
      columns: this.options.columns,
      emptyViewModel: this.options.emptyViewModel
    }))
  },

  behaviors: {
    collectionBulk: {
      behaviorClass: BulkToolsBehavior
    },
    collectionPagination: {
      behaviorClass: CollectionPaginationBehavior
    }
  },
  templateContext: function () {
    var self = this
    return {
      hasBulk: function () {
        if (_.size(_.get(self.options, 'columns._bulk.options')) <= 0) return false
        return true
      },
      getHideClasses: App.TemplateHelpers.getHideClasses(self.options), 
      // check wheter all items in the collection are selected
      areAllSelected: function () {
        if (self.collection.length <= 0) return false
        return self.collection.where({'_bulk': true}).length === self.collection.length
      },
      getListColumns: function () {
        var columns = _.pickBy(self.options.columns, function (o) {
          return App.session.user.hasRole(o.roles)
        })
        return columns
      },
      getPaginationData: function () {
        if (!self.collection.state) return false
        return self.collection.state
      },
      getDeviceTypeLabel: App.TemplateHelpers.getDeviceTypeLabel,
      userHasRole: App.TemplateHelpers.userHasRole

    }
  }
})

module.exports.ListView = ListView



// WEBPACK FOOTER //
// ./js/components/list-view/index.js